import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FiltersType, FilterType } from 'store/global/types';
import { DefaultFiltersType, JobParamsType } from 'store/auth/types';

import { Box, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ParametersType as MessageParameterType } from 'components/JobGenerator/types';
import Instructions from './components/Instruction';

import {
    AdditionalSettingsType,
    ColorsType,
    ExampleType,
    ParamsType,
    StateTypes,
    TypesOfFiltersType,
} from './types';
import {
    addFirestoreDocs,
    auth,
    logEventFirebase,
    onUpdateDoc,
    setFirestoreDoc,
} from 'utils/config/firebase';
import {
    ADDITIONAL_SETTINGS_INSTRUCTION,
    INSTRUCTION_HISTORY,
    USER_DEFAULT_FILTERS,
} from 'utils/config/constants/controllerNames';

import {
    getDefaultFilters,
    getFormattedFilters,
    getTranslatedLabels,
    normalizeFilters,
} from 'helpers/filters';
import FilterContainer from './components/FilterContainer';
import { DEFAULT_FILTERS } from 'utils/config/constants';
import Personalize from './components/Personalize/index';
import { saveFiltersMessage } from 'helpers/messages';
import Example from './components/Example/index';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'app/hooks';
import { COLORS } from './colors';
import { Storage } from 'storage';
import { sanitizeAndDeleteHtml } from 'helpers/htmlParser';
import { deleteField } from 'firebase/firestore';

function isTrue(item: FilterType, value: string | number) {
    if (item.prompt === value || item.id === value || item.value == value) {
        return true;
    }
    if (!item.additional) {
        return false;
    }
    for (const key in item.additional) {
        if (item.additional[key] === value) {
            return true;
        }
    }
    return false;
}

function extractStructures(
    structures: string | number[],
    additionalSettings: FiltersType,
): number[] {
    if (!structures) {
        return [1, 2];
    }

    if (Array.isArray(structures)) {
        return structures;
    }

    const splitted: string[] = structures.split(' , ');
    let result: number[] = [];
    splitted.forEach((splitItem) => {
        const found = additionalSettings.STRUCTURES.find(
            (item) => item?.value.toString(10) === splitItem || item?.prompt === splitItem,
        );
        if (found?.id) {
            result.push(found.id);
        }
    });

    return result;
}

function setFilters(
    parameters: MessageParameterType | undefined,
    additionalSettings: FiltersType,
    getFilters: () => DefaultFiltersType,
) {
    const userDefaultFilters = getFilters();
    if (parameters) {
        const params: ParamsType = {
            anonymized: parameters?.anonymized ? 'yes' : 'no',
            formality:
                additionalSettings.FORMALITY_MARKS?.find((item) =>
                    isTrue(item, parameters.formality),
                )?.value ?? 6,
            instructions: userDefaultFilters?.instructions ?? '',
            length:
                additionalSettings.LENGTH_MARKS.find((item) => isTrue(item, parameters.length))
                    ?.value ?? DEFAULT_FILTERS.length,
            structures: extractStructures(parameters.structures, additionalSettings),
            tone_of_voice:
                additionalSettings.TONES_OF_VOICE?.find((item) =>
                    isTrue(item, parameters.tone_of_voice),
                )?.id ?? 1,
            urgency:
                additionalSettings.URGENCY_MARKS.find((item) => isTrue(item, parameters.urgency))
                    ?.value ?? 1,
        };
        return params;
    }
    const params: ParamsType = {
        anonymized: userDefaultFilters?.anonymized === 'yes' ? 'yes' : 'no',
        formality: userDefaultFilters?.formality ?? DEFAULT_FILTERS.formality,
        instructions: userDefaultFilters?.instructions ?? DEFAULT_FILTERS.instructions,
        length: userDefaultFilters?.length ?? DEFAULT_FILTERS.length,
        structures: userDefaultFilters?.structures ?? DEFAULT_FILTERS.structures,
        tone_of_voice: userDefaultFilters?.tone_of_voice ?? DEFAULT_FILTERS.tone_of_voice,
        urgency: userDefaultFilters?.urgency ?? DEFAULT_FILTERS.urgency,
    };
    return params;
}

const AdditionalSettings: FC<AdditionalSettingsType> = ({
    onCloseModal,
    onGenerate,
    parameters,
    setIsExpended,
    country,
}) => {
    const { t } = useTranslation();
    const { userDefaultFilters, userSettings, defaultFilters } = useAppSelector(
        (state) => state.auth,
    );
    const { document_id } = useParams();

    const { additionalSettings, instructionHistory, targetCountries } = useAppSelector(
        (state) => state.global,
    );

    const [state, setState] = useState<StateTypes>(DEFAULT_FILTERS);
    const [isCheckedAsDefault, setIsCheckedIsDefault] = useState<boolean>(false);
    const [isCheckedForFeatureVersions, setIsCheckedForFeatureVersions] = useState<boolean>(false);
    const [classNames, setClassNames] = useState({
        container: '',
        example: '',
    });

    const [example, setExample] = useState<ExampleType>({
        label: '',
        id: '',
    });
    const isHiddenExamples = !!userSettings?.dont_show_additional_settings_example;

    const getFilters = useCallback(() => {
        if (userDefaultFilters) {
            return normalizeFilters(userDefaultFilters, additionalSettings);
        }
        return getDefaultFilters(userSettings, defaultFilters);
    }, [additionalSettings, defaultFilters, userDefaultFilters, userSettings]);

    useEffect(() => {
        if (example.id) {
            setIsExpended(true);
        }
    }, [example.id, setIsExpended]);

    useEffect(() => {
        if (additionalSettings) {
            const params = setFilters(parameters, additionalSettings, getFilters);
            const normalizedParams = params ? normalizeFilters(params, additionalSettings) : null;
            const savedInstruction = Storage.getItem('additional_settings_instruction');
            setState({
                anonymized: normalizedParams?.anonymized ?? DEFAULT_FILTERS.anonymized,
                formality: normalizedParams?.formality ?? DEFAULT_FILTERS.formality,
                hasChanged: false,
                instructions:
                    normalizedParams?.instructions ||
                    (!document_id && savedInstruction) ||
                    DEFAULT_FILTERS.instructions,
                length: normalizedParams?.length ?? DEFAULT_FILTERS.length,
                structures: normalizedParams?.structures ?? DEFAULT_FILTERS.structures,
                tone_of_voice: normalizedParams?.tone_of_voice ?? DEFAULT_FILTERS.tone_of_voice,
                urgency: normalizedParams?.urgency ?? DEFAULT_FILTERS.urgency,
            });
        }
    }, [additionalSettings, document_id, getFilters, parameters]);

    const handleChange = (field: string, value: number | number[] | string | string[]) => {
        setState((prevState) => ({ ...prevState, [field]: value }));
    };

    const updateFilters = (doc_id?: string) => {
        const filterDocumentId = document_id ?? doc_id;
        if (isCheckedAsDefault) {
            const data = {
                formality: state.formality,
                urgency: state.urgency,
                length: state.length,
                anonymized: state.anonymized,
                structures: state.structures,
                tone_of_voice: state.tone_of_voice,
            };
            if (userDefaultFilters?.document_id) {
                onUpdateDoc(USER_DEFAULT_FILTERS, userDefaultFilters.document_id as string, {
                    ...data,
                    // remove old key
                    toneOfVoice: deleteField(),
                    user_id: auth?.currentUser?.uid,
                });
            } else {
                addFirestoreDocs(USER_DEFAULT_FILTERS, {
                    ...data,
                    user_id: auth?.currentUser?.uid,
                });
            }
        }

        const cleanedInstructions = sanitizeAndDeleteHtml(state.instructions.trim());
        if (isCheckedForFeatureVersions && state.instructions) {
            if (filterDocumentId) {
                return setFirestoreDoc(ADDITIONAL_SETTINGS_INSTRUCTION, filterDocumentId, {
                    value: cleanedInstructions,
                });
            }
            Storage.setItem('additional_settings_instruction', cleanedInstructions);
        }
        if (state.instructions && state.instructions.trim() !== instructionHistory?.[0]?.value) {
            addFirestoreDocs(INSTRUCTION_HISTORY, {
                user_id: auth?.currentUser?.uid,
                value: cleanedInstructions,
                created_at: new Date().toISOString(),
            });
        }
    };

    const onSubmit = () => {
        const country_code =
            targetCountries?.find((item) => item.title === country)?.country_code ??
            userSettings?.target_country_code;
        const params = getFormattedFilters(
            state,
            additionalSettings,
            country_code,
        ) as JobParamsType;
        logEventFirebase('additional_settings', params);
        if (onGenerate) {
            onGenerate(params, updateFilters);
        }
        if (document_id) {
            updateFilters();
        }
        onCloseModal();
    };

    const formatValue = (type: ColorsType, marks: TypesOfFiltersType) => {
        const data = getTranslatedLabels(additionalSettings[marks], type);
        return data.map((item, i) => ({
            ...item,
            color: COLORS[type][i].color,
            backgroundColor: COLORS[type][i].backgroundColor,
        }));
    };

    const onOpenExamples = (data: ExampleType) => {
        setExample(data);
        setClassNames({ container: 'expanded', example: 'add-set-examples-active' });
    };

    const onCloseExamples = () => {
        setClassNames({ container: 'collapse', example: 'add-set-examples-collapse' });
        setTimeout(() => {
            setExample({
                label: '',
                id: '',
            });
        }, 1000);
    };

    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
        setIsCheckedIsDefault(e.target.checked);
    };
    const handleCheckboxForFeatureVersions = (e: ChangeEvent<HTMLInputElement>) => {
        setIsCheckedForFeatureVersions(e.target.checked);
    };

    const onlySave = () => {
        updateFilters();
        onCloseModal();
        saveFiltersMessage();
    };

    return (
        <Box className={`additional-settings-container`}>
            <Box className={`additional-settings-form-container ${classNames.container}`}>
                <CloseIcon className="close-icon" onClick={onCloseModal} />
                <Box className="settings-container">
                    <Box className="add-set-pref">
                        <Box className="add-set-pref-header">
                            <h3>{t('web_app.additiona_settings.filters.title')}</h3>
                            <Box className="add-set-pref-header-checkbox">
                                <Checkbox
                                    checked={isCheckedAsDefault}
                                    onChange={handleCheckboxChange}
                                />
                                {t('web_app.additiona_settings.filters.save_default')}
                            </Box>
                        </Box>
                        <Box className="add-set-pref-body">
                            <FilterContainer
                                name="length"
                                id="length_filter"
                                items={formatValue('length', 'LENGTH_MARKS')}
                                onOpenExamples={onOpenExamples}
                                value={state.length}
                                label={t('web_app.additional_settings.length_title')}
                                onChange={handleChange}
                                isHiddenExamples={isHiddenExamples}
                                isOpenExamples={!!example.id}
                                helpMessage={t('web_app.additional_settings.length_tooltip')}
                                additionalClassName="add-set-pref-length"
                            />
                            <FilterContainer
                                name="formality"
                                id="formality_filter"
                                items={formatValue('formality', 'FORMALITY_MARKS')}
                                onOpenExamples={onOpenExamples}
                                value={state.formality}
                                label={t('web_app.additional_settings.formality_title')}
                                onChange={handleChange}
                                isHiddenExamples={isHiddenExamples}
                                isOpenExamples={!!example.id}
                                helpMessage={t('web_app.additional_settings.formality_tooltip')}
                            />
                            <FilterContainer
                                name="urgency"
                                id="urgency_filter"
                                items={formatValue('urgency', 'URGENCY_MARKS')}
                                onOpenExamples={onOpenExamples}
                                label={t('web_app.additional_settings.urgency_title')}
                                value={state.urgency}
                                onChange={handleChange}
                                isHiddenExamples={isHiddenExamples}
                                isOpenExamples={!!example.id}
                                helpMessage={t('web_app.additional_settings.urgency_tooltip')}
                            />
                            <Personalize
                                onChange={handleChange}
                                toneOfVoice={state.tone_of_voice}
                                structures={state.structures}
                                anonymized={state.anonymized}
                            />
                        </Box>
                    </Box>
                    <Box className={`add-set-examples ${classNames.example}`}>
                        <Example
                            onCloseExamples={onCloseExamples}
                            label={example.label}
                            exampleId={example.id}
                        />
                    </Box>
                    <Box className="add-set-instruct">
                        <Box className="add-set-instruct-header">
                            <h3>{t('web_app.additiona_settings.instructions_title')}</h3>
                        </Box>
                        <Box className="add-set-instruct-body">
                            <Instructions
                                onChange={handleChange}
                                value={state.instructions}
                                handleCheckboxForFeatureVersions={handleCheckboxForFeatureVersions}
                                isCheckedForFeatureVersions={isCheckedForFeatureVersions}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box className="settings-container-footer">
                    <Box className="settings-container-footer-btns">
                        <button
                            onClick={onlySave}
                            disabled={!isCheckedForFeatureVersions && !isCheckedAsDefault}
                        >
                            {t('web_app.save_btn')}
                        </button>
                        <button onClick={onSubmit}>
                            {t('web_app.additiona_settings.save_and_regenerate')}
                        </button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default AdditionalSettings;
